import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useLivePositions } from '@/hooks/usePositions'
import { useAuth } from '@/hooks/useAuth'
import { PositionManager } from '@/components/positions'
import type { PositionActionRequest, PositionManagerAction } from '@/components/positions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { TrendingUp, TrendingDown, Clock, Users, ArrowRight, FileEdit, Lock } from 'lucide-react'
import type { LivePosition } from '@/lib/api'

function PositionCard({
  pos,
  canManage,
  onAction,
}: {
  pos: LivePosition
  canManage: boolean
  onAction: (action: PositionManagerAction, positionId: string) => void
}) {
  const direction = (pos.direction || '').toUpperCase()
  const isLong = direction === 'LONG'
  const isShort = direction === 'SHORT'
  const directionLabel = direction || 'N/A'
  const pnl = pos.pnl_pct

  const formatPrice = (price: number | null) => {
    if (!price) return '—'
    return price < 1 ? `$${price.toFixed(4)}` : `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  }

  return (
    <div className="flex items-center gap-4 rounded-lg border p-3 transition-colors hover:bg-muted/40">
      {/* Direction & Symbol */}
      <div className="flex items-center gap-2 min-w-[100px]">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg',
            isLong
              ? 'bg-green-500/15 text-green-500'
              : isShort
                ? 'bg-red-500/15 text-red-500'
                : 'bg-muted text-muted-foreground'
          )}
        >
          {isLong ? (
            <TrendingUp className="h-4 w-4" />
          ) : isShort ? (
            <TrendingDown className="h-4 w-4" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
        </div>
        <div>
          <span className="font-semibold text-sm">{pos.symbol}</span>
          <div className="text-xs text-muted-foreground">{directionLabel}</div>
        </div>
      </div>

      {/* Price & PnL */}
      <div className="flex-1 min-w-[140px]">
        <div className="text-sm font-medium">
          {formatPrice(pos.current_price)}
        </div>
        {pos.entry_price && (
          <div className="text-xs text-muted-foreground">
            Entry: {formatPrice(pos.entry_price)}
          </div>
        )}
      </div>

      {/* PnL Badge */}
      <div className="min-w-[70px] text-right">
        {pnl !== null ? (
          <Badge
            className={cn(
              'text-xs font-semibold tabular-nums',
              pnl >= 0
                ? 'bg-green-500/15 text-green-500 hover:bg-green-500/25'
                : 'bg-red-500/15 text-red-500 hover:bg-red-500/25'
            )}
          >
            {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">N/A</Badge>
        )}
      </div>

      {/* Duration */}
      <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground min-w-[60px]">
        <Clock className="h-3 w-3" />
        {pos.duration || '—'}
      </div>

      {/* Users */}
      <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground">
        <Users className="h-3 w-3" />
        {pos.users_in_position?.length || 0}
      </div>

      {/* Direct position actions */}
      <div className="flex items-center gap-1">
        {canManage && (
          <>
            <Button size="sm" variant="outline" onClick={() => onAction('update', pos.id)}>
              <FileEdit className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">Update</span>
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onAction('close', pos.id)}>
              <Lock className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">Close</span>
            </Button>
          </>
        )}
        <Button asChild size="icon" variant="ghost">
          <Link to={`/positions?status=open&symbol=${encodeURIComponent(pos.symbol)}`} aria-label={`View ${pos.symbol} position`}>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  )
}

export function LivePositionsWidget() {
  const { data, isLoading } = useLivePositions()
  const { hasPermission } = useAuth()
  const [actionRequest, setActionRequest] = useState<PositionActionRequest | null>(null)
  const positions = data?.positions || []
  const canManage = hasPermission('manage_positions')

  const requestAction = (action: PositionManagerAction, positionId: string) => {
    setActionRequest({ requestId: Date.now(), action, positionId })
  }

  if (isLoading) {
    return null // Don't flash loading state
  }

  if (positions.length === 0) {
    return null // Don't show widget when no open positions
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            Open Positions
            <Badge variant="secondary" className="text-xs ml-1">
              {positions.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {positions.map((pos) => (
            <PositionCard key={pos.id} pos={pos} canManage={canManage} onAction={requestAction} />
          ))}
        </CardContent>
      </Card>
      <PositionManager actionRequest={actionRequest} hideTrigger />
    </>
  )
}
